import {useEffect, useState} from 'react';
import {Layout} from '@/components/Layout';
import {Plus, Building2, Factory, Zap, Users as UsersIcon, Edit, Trash2, Eye, ShieldOff} from 'lucide-react';
import {Button} from '@/components/ui/button';
import {Card} from '@/components/ui/card';
import {Badge} from '@/components/ui/badge';
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter} from '@/components/ui/dialog';
import {Label} from '@/components/ui/label';
import {Input} from '@/components/ui/input';
import {Textarea} from '@/components/ui/textarea';
import {Checkbox} from '@/components/ui/checkbox';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select';
import {toast} from 'sonner';
import {api, departmentEndpoints, getApiErrorMessage, objectColorEndpoints, objectIconEndpoints, userEndpoints} from '@/lib/api';
import {withoutUuid} from '@/lib/display';
import {usePermissions} from '@/hooks/usePermissions';
import {PERMISSIONS} from '@/lib/permissions';

const iconMap = {
    zap: Zap,
    building: Building2,
    factory: Factory
};

const colorClasses = {
    blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-700',
        icon: 'text-blue-600',
        badge: 'bg-blue-100 text-blue-700'
    },
    emerald: {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        text: 'text-emerald-700',
        icon: 'text-emerald-600',
        badge: 'bg-emerald-100 text-emerald-700'
    },
    amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-700',
        icon: 'text-amber-600',
        badge: 'bg-amber-100 text-amber-700'
    },
    purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        text: 'text-purple-700',
        icon: 'text-purple-600',
        badge: 'bg-purple-100 text-purple-700'
    },
    slate: {
        bg: 'bg-slate-50',
        border: 'border-slate-200',
        text: 'text-slate-700',
        icon: 'text-slate-600',
        badge: 'bg-slate-100 text-slate-700'
    }
};

const collectionPayload = (payload, key) => {
    const source = payload?.data ?? payload;
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.data)) return source.data;
    if (Array.isArray(source?.[key])) return source[key];
    return [];
};

const idFrom = (source) => source?.id ?? source?.object_color_id ?? source?.object_icon_id ?? '';

const normalizeOption = (source = {}) => {
    const id = idFrom(source);
    const label = source.label || source.name || source.title || String(id);
    return {
        ...source,
        id,
        label,
        name: label,
        color: source.color || source.hex || source.hex_color || source.value || '',
        icon: source.icon || source.icon_name || source.name || '',
    };
};

const ColorOption = ({color}) => (
    <span className="inline-flex items-center gap-2 min-w-0">
    <span className="w-3 h-3 rounded-full border border-slate-200 flex-shrink-0" style={{backgroundColor: color?.color || '#64748b'}}/>
    <span className="truncate">{color?.label || color?.name || 'Sem nome'}</span>
  </span>
);

const IconOption = ({icon}) => (
    <span className="inline-flex items-center gap-2 min-w-0">
    {icon?.icon ? (
        <img src={icon.icon} alt="" className="w-4 h-4 object-contain flex-shrink-0"/>
    ) : (
        <Building2 size={14} className="text-slate-500 flex-shrink-0"/>
    )}
        <span className="truncate">{icon?.label || icon?.name || 'Sem nome'}</span>
  </span>
);

const numberFrom = (...values) => {
    const value = values.find(v => v !== undefined && v !== null);
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const arrayFrom = (value) => {
    if (Array.isArray(value)) return value;
    if (value === undefined || value === null || value === '') return [];
    return [value];
};

const normalizeManagers = (department) => {
    const managers = department?.managers || department?.responsibles || department?.users || [];
    if (!Array.isArray(managers)) return [];
    return managers.map(manager => (
        typeof manager === 'string' ? manager : manager?.name || manager?.email || ''
    )).filter(Boolean);
};

const normalizeManagerIds = (department) =>
    arrayFrom(department?.manager_ids ?? department?.managerIds ?? department?.user_ids ?? department?.userIds)
        .map(value => String(typeof value === 'object' ? value?.id ?? value?.user_id : value))
        .filter(Boolean);

const normalizeManagerOptions = (payload) =>
    collectionPayload(payload, 'users')
        .map((user = {}, index = 0) => {
            const attributes = user?.attributes && typeof user.attributes === 'object' && !Array.isArray(user.attributes)
                ? user.attributes
                : {};
            const source = {...user, ...attributes};
            const firstName = source.first_name ?? source.firstName ?? source.name ?? '';
            const lastName = source.last_name ?? source.lastName ?? source.surname ?? '';
            const fullName = `${firstName} ${lastName}`.trim();
            const name = source.full_name || source.display_name || fullName || source.email || `Gestor ${index + 1}`;
            const roles = arrayFrom(source.roles).map(role => (
                typeof role === 'string' ? role : role?.name || role?.slug || ''
            ));

            return {
                id: source.id ?? source.user_id ?? index + 1,
                name,
                email: source.email ?? '',
                status: source.status ?? 'active',
                roles,
            };
        })
        .filter(user => (
            user.id &&
            user.status === 'active' &&
            (user.roles.length === 0 || user.roles.some(role => ['gestor', 'admin', 'superadmin'].includes(String(role).toLowerCase())))
        ));

const normalizeDepartment = (department = {}, index = 0) => ({
    id: department.id ?? department.department_id ?? department.uuid ?? index + 1,
    uuid: department.uuid,
    name: department.name || department.label || `Departamento ${index + 1}`,
    description: department.description || department.notes || '',
    icon: department.icon || department.icon_name || 'building',
    color: department.color || department.color_name || 'blue',
    object_icon_id: department.object_icon_id ?? department.objectIconId ?? null,
    object_color_id: department.object_color_id ?? department.objectColorId ?? null,
    totalTags: numberFrom(department.totalTags, department.total_tags, department.tags_count),
    activeUsers: numberFrom(department.activeUsers, department.active_users, department.users_count),
    backofficeUsers: numberFrom(department.backofficeUsers, department.backoffice_users, department.managers_count),
    totalTickets: numberFrom(department.totalTickets, department.total_tickets, department.tickets_count),
    openTickets: numberFrom(department.openTickets, department.open_tickets),
    managerIds: normalizeManagerIds(department),
    managers: normalizeManagers(department),
});

const normalizeDepartments = (payload) =>
    collectionPayload(payload, 'departments').map(normalizeDepartment);

const emptyDepartmentForm = {
    name: '',
    description: '',
    object_icon_id: '',
    object_color_id: '',
    manager_ids: [],
};

const departmentPayload = (form) => ({
    name: form.name.trim(),
    description: form.description.trim() || null,
    object_icon_id: form.object_icon_id ? Number(form.object_icon_id) : null,
    object_color_id: form.object_color_id ? Number(form.object_color_id) : null,
    manager_ids: form.manager_ids.map(managerId => Number(managerId)),
});

export const CategoriesManagement = () => {
    const {can} = usePermissions();
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [editingDepartment, setEditingDepartment] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [objectColors, setObjectColors] = useState([]);
    const [objectIcons, setObjectIcons] = useState([]);
    const [managerOptions, setManagerOptions] = useState([]);
    const [managersLoading, setManagersLoading] = useState(false);
    const [departmentsLoading, setDepartmentsLoading] = useState(true);
    const [departmentsError, setDepartmentsError] = useState('');
    const [form, setForm] = useState(emptyDepartmentForm);
    const [submitting, setSubmitting] = useState(false);

    const canReadDepartments = can(PERMISSIONS.DEPARTMENT_READ);
    const canCreateDepartments = can(PERMISSIONS.DEPARTMENT_CREATE);
    const canUpdateDepartments = can(PERMISSIONS.DEPARTMENT_UPDATE);
    const canDeleteDepartments = can(PERMISSIONS.DEPARTMENT_DELETE);
    const canReadUsers = can(PERMISSIONS.USER_READ);

    useEffect(() => {
        if (!canReadDepartments) {
            setDepartments([]);
            setObjectColors([]);
            setObjectIcons([]);
            setManagerOptions([]);
            setDepartmentsLoading(false);
            setManagersLoading(false);
            setDepartmentsError('');
            return undefined;
        }

        let cancelled = false;

        const loadDepartments = async () => {
            setDepartmentsLoading(true);
            setManagersLoading(canReadUsers);
            setDepartmentsError('');

            try {
                const [departmentsResponse, colorsResponse, iconsResponse, usersResponse] = await Promise.all([
                    api.get(departmentEndpoints.list),
                    api.get(objectColorEndpoints.list).catch(() => ({data: []})),
                    api.get(objectIconEndpoints.list).catch(() => ({data: []})),
                    canReadUsers ? api.get(userEndpoints.list).catch(() => ({data: []})) : Promise.resolve({data: []}),
                ]);
                if (!cancelled) {
                    setDepartments(normalizeDepartments(departmentsResponse.data));
                    setObjectColors(collectionPayload(colorsResponse.data, 'colors').map(normalizeOption).filter(option => option.id));
                    setObjectIcons(collectionPayload(iconsResponse.data, 'icons').map(normalizeOption).filter(option => option.id));
                    setManagerOptions(normalizeManagerOptions(usersResponse.data));
                }
            } catch (error) {
                if (!cancelled) {
                    setDepartments([]);
                    setDepartmentsError(getApiErrorMessage(error, 'Não foi possível carregar os departamentos.'));
                }
            } finally {
                if (!cancelled) {
                    setDepartmentsLoading(false);
                    setManagersLoading(false);
                }
            }
        };

        loadDepartments();

        return () => {
            cancelled = true;
        };
    }, [canReadDepartments, canReadUsers]);

    const totalStats = {
        categories: departments.length,
        totalTags: departments.reduce((acc, cat) => acc + cat.totalTags, 0),
        totalUsers: departments.reduce((acc, cat) => acc + cat.activeUsers, 0),
        totalTickets: departments.reduce((acc, cat) => acc + cat.totalTickets, 0)
    };

    const selectedColor = objectColors.find(color => String(color.id) === String(form.object_color_id));
    const selectedIcon = objectIcons.find(icon => String(icon.id) === String(form.object_icon_id));
    const selectedCategoryColors = selectedCategory ? (colorClasses[selectedCategory.color] || colorClasses.blue) : colorClasses.blue;
    const SelectedCategoryIcon = selectedCategory ? (iconMap[selectedCategory.icon] || Building2) : Building2;
    const selectedCategoryColorOption = selectedCategory
        ? objectColors.find(color => String(color.id) === String(selectedCategory.object_color_id))
        : null;
    const selectedCategoryIconOption = selectedCategory
        ? objectIcons.find(icon => String(icon.id) === String(selectedCategory.object_icon_id))
        : null;

    const openCreate = () => {
        if (!canCreateDepartments) {
            toast.error('Nao tem permissão para criar categorias.');
            return;
        }
        setEditingDepartment(null);
        setForm(emptyDepartmentForm);
        setIsCreateDialogOpen(true);
    };

    const openDetails = (department) => {
        setSelectedCategory(department);
    };

    const openEdit = (department) => {
        if (!canUpdateDepartments) {
            toast.error('Nao tem permissão para editar categorias.');
            return;
        }
        setEditingDepartment(department);
        setForm({
            name: department.name || '',
            description: department.description || '',
            object_icon_id: department.object_icon_id ? String(department.object_icon_id) : '',
            object_color_id: department.object_color_id ? String(department.object_color_id) : '',
            manager_ids: department.managerIds || [],
        });
        setIsCreateDialogOpen(true);
    };

    const toggleManager = (managerId) => {
        setForm(prev => {
            const id = String(managerId);
            const managerIds = prev.manager_ids.includes(id)
                ? prev.manager_ids.filter(item => item !== id)
                : [...prev.manager_ids, id];

            return {...prev, manager_ids: managerIds};
        });
    };

    const handleSubmit = async () => {
        if (editingDepartment && !canUpdateDepartments) {
            toast.error('Nao tem permissão para editar categorias.');
            return;
        }
        if (!editingDepartment && !canCreateDepartments) {
            toast.error('Nao tem permissão para criar categorias.');
            return;
        }
        if (!form.name.trim()) {
            toast.error('Nome da categoria é obrigatório.');
            return;
        }
        if (form.manager_ids.length === 0) {
            toast.error('Associe pelo menos um gestor.');
            return;
        }

        setSubmitting(true);
        try {
            if (editingDepartment) {
                const response = await api.post(departmentEndpoints.update, {
                    id: editingDepartment.id,
                    uuid: editingDepartment.uuid,
                    ...departmentPayload(form),
                });
                const updated = normalizeDepartment(response.data?.data ?? response.data?.department ?? response.data, 0);
                setDepartments(prev => prev.map(department => department.id === editingDepartment.id ? updated : department));
                toast.success(`Categoria "${updated.name}" atualizada com sucesso!`);
            } else {
                const response = await api.post(departmentEndpoints.create, departmentPayload(form));
                const created = normalizeDepartment(response.data?.data ?? response.data?.department ?? response.data, 0);
                setDepartments(prev => [created, ...prev]);
                toast.success(`Categoria "${created.name}" criada com sucesso!`);
            }
            setIsCreateDialogOpen(false);
            setEditingDepartment(null);
            setForm(emptyDepartmentForm);
        } catch (error) {
            toast.error(getApiErrorMessage(error, 'Não foi possível guardar a categoria.'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (department) => {
        if (!canDeleteDepartments) {
            toast.error('Nao tem permissão para eliminar categorias.');
            return;
        }
        if (!window.confirm(`Eliminar categoria "${department.name}"?`)) return;

        setSubmitting(true);
        try {
            await api.post(departmentEndpoints.delete, {
                id: department.id,
                uuid: department.uuid,
            });
            setDepartments(prev => prev.filter(item => item.id !== department.id));
            toast.success(`Categoria "${department.name}" removida.`);
        } catch (error) {
            toast.error(getApiErrorMessage(error, 'Não foi possível remover a categoria.'));
        } finally {
            setSubmitting(false);
        }
    };

    if (!canReadDepartments) {
        return (
            <Layout>
                <div className="min-h-[60vh] flex items-center justify-center">
                    <Card className="p-8 text-center max-w-md">
                        <div className="w-14 h-14 rounded-2xl bg-red-100 mx-auto mb-4 flex items-center justify-center">
                            <ShieldOff className="text-red-600" size={28}/>
                        </div>
                        <h1 className="text-xl font-secondary font-bold text-slate-900">Acesso Negado</h1>
                        <p className="text-sm text-slate-600 mt-2">
                            Nao tem permissao para consultar categorias.
                        </p>
                    </Card>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-slate-900 text-3xl font-secondary font-bold tracking-tight">Gestão de Categorias</h1>
                        <p className="text-slate-600 font-primary mt-1">Organizar sistema por filiais e segmentar perfis de utilizador</p>
                    </div>
                    <Dialog
                        open={isCreateDialogOpen && (editingDepartment ? canUpdateDepartments : canCreateDepartments)}
                        onOpenChange={(open) => setIsCreateDialogOpen(open && (editingDepartment ? canUpdateDepartments : canCreateDepartments))}
                    >
                        {canCreateDepartments && (
                            <Button data-testid="create-category-btn" className="bg-blue-600 hover:bg-blue-700" onClick={openCreate}>
                                <Plus size={18} className="mr-2"/>
                                Nova Categoria
                            </Button>
                        )}
                        <DialogContent className="max-w-2xl">
                            <DialogHeader>
                                <DialogTitle>{editingDepartment ? 'Editar Categoria' : 'Criar Nova Categoria'}</DialogTitle>
                                <DialogDescription>
                                    Criar segmento para organizar tags, utilizadores e permissões
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                    <Label htmlFor="cat-name">Nome da Categoria *</Label>
                                    <Input id="cat-name" value={form.name} onChange={event => setForm(prev => ({...prev, name: event.target.value}))} placeholder="Ex: Frota Elétrica, Escritório, Fábrica 1"/>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="cat-description">Descrição</Label>
                                    <Textarea
                                        id="cat-description"
                                        value={form.description}
                                        onChange={event => setForm(prev => ({...prev, description: event.target.value}))}
                                        placeholder="Descreva o propósito desta categoria..."
                                        rows={3}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="cat-icon">Ícone</Label>
                                        <Select value={form.object_icon_id || 'none'} onValueChange={value => setForm(prev => ({...prev, object_icon_id: value === 'none' ? '' : value}))}>
                                            <SelectTrigger id="cat-icon">
                                                <SelectValue placeholder="Selecionar Ícone">
                                                    {form.object_icon_id ? <IconOption icon={selectedIcon}/> : 'Sem Ícone'}
                                                </SelectValue>
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">Sem Ícone</SelectItem>
                                                {objectIcons.map(icon => (
                                                    <SelectItem key={icon.id} value={String(icon.id)}>
                                                        <IconOption icon={icon}/>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="cat-color">Cor</Label>
                                        <Select value={form.object_color_id || 'none'} onValueChange={value => setForm(prev => ({...prev, object_color_id: value === 'none' ? '' : value}))}>
                                            <SelectTrigger id="cat-color">
                                                <SelectValue placeholder="Selecionar cor">
                                                    {form.object_color_id ? <ColorOption color={selectedColor}/> : 'Sem cor'}
                                                </SelectValue>
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">Sem cor</SelectItem>
                                                {objectColors.map(color => (
                                                    <SelectItem key={color.id} value={String(color.id)}>
                                                        <ColorOption color={color}/>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label>Gestores *</Label>
                                    <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-white">
                                        {managersLoading ? (
                                            <div className="p-3 text-sm text-slate-500">A carregar gestores...</div>
                                        ) : managerOptions.length === 0 ? (
                                            <div className="p-3 text-sm text-slate-500">Sem gestores ativos disponiveis.</div>
                                        ) : managerOptions.map(manager => (
                                            <label key={manager.id} className="flex cursor-pointer items-center gap-3 border-b border-slate-100 p-3 last:border-b-0 hover:bg-slate-50">
                                                <Checkbox
                                                    checked={form.manager_ids.includes(String(manager.id))}
                                                    onCheckedChange={() => toggleManager(manager.id)}
                                                />
                                                <span className="min-w-0 flex-1">
                                                    <span className="block truncate text-sm font-medium text-slate-900">{manager.name}</span>
                                                    {manager.email && <span className="block truncate text-xs text-slate-500">{manager.email}</span>}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                    <p className="text-sm font-medium text-blue-900 mb-2">📂 Segmentação por Filial/Categoria</p>
                                    <ul className="text-xs text-blue-700 space-y-1">
                                        <li>• Tags ficam organizadas por filial/categoria</li>
                                        <li>• Utilizadores atribuídos a filiais específicas</li>
                                        <li>• Gestores de filial veem apenas sua(s) área(s)</li>
                                        <li>• Tickets filtrados automaticamente por filial</li>
                                        <li>• Dashboards e relatórios segmentados por filial</li>
                                    </ul>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)} disabled={submitting}>Cancelar</Button>
                                <Button
                                    className="bg-blue-600 hover:bg-blue-700"
                                    onClick={handleSubmit}
                                    disabled={submitting || (editingDepartment ? !canUpdateDepartments : !canCreateDepartments)}
                                >
                                    {submitting ? 'A guardar...' : editingDepartment ? 'Guardar Categoria' : 'Criar Categoria'}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <Card className="p-6">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-slate-600 text-sm font-medium">Total Categorias</p>
                            <Building2 className="text-blue-600" size={20}/>
                        </div>
                        <p className="text-3xl font-secondary font-bold text-slate-900">{totalStats.categories}</p>
                        <p className="text-xs text-slate-500 mt-1">Segmentos ativos</p>
                    </Card>

                    <Card className="p-6">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-slate-600 text-sm font-medium">Total Tags</p>
                            <Building2 className="text-emerald-600" size={20}/>
                        </div>
                        <p className="text-3xl font-secondary font-bold text-slate-900">{totalStats.totalTags}</p>
                        <p className="text-xs text-slate-500 mt-1">Em todas categorias</p>
                    </Card>

                    <Card className="p-6">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-slate-600 text-sm font-medium">Total Utilizadores</p>
                            <UsersIcon className="text-amber-600" size={20}/>
                        </div>
                        <p className="text-3xl font-secondary font-bold text-slate-900">{totalStats.totalUsers}</p>
                        <p className="text-xs text-slate-500 mt-1">Utilizadores ativos</p>
                    </Card>

                    <Card className="p-6">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-slate-600 text-sm font-medium">Total Tickets</p>
                            <Building2 className="text-purple-600" size={20}/>
                        </div>
                        <p className="text-3xl font-secondary font-bold text-slate-900">{totalStats.totalTickets}</p>
                        <p className="text-xs text-slate-500 mt-1">Todas categorias</p>
                    </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                    {(departmentsLoading || departmentsError || departments.length === 0) ? (
                        <Card className="p-10 text-center text-slate-500 lg:col-span-2 xl:col-span-3">
                            {departmentsLoading ? 'A carregar departamentos...' : departmentsError || 'Sem departamentos para mostrar.'}
                        </Card>
                    ) : departments.map((category) => {
                        const Icon = iconMap[category.icon] || Building2;
                        const colors = colorClasses[category.color] || colorClasses.blue;

                        return (
                            <Card
                                key={category.id}
                                className={`p-6 border-2 hover:shadow-lg transition-all cursor-pointer ${colors.border}`}
                                onClick={() => openDetails(category)}
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className={`${colors.bg} p-3 rounded-xl`}>
                                            <Icon className={colors.icon} size={28}/>
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-secondary font-bold text-slate-900">{category.name}</h3>
                                            <p className="text-xs text-slate-600 mt-1">{category.description}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div className={`p-3 ${colors.bg} rounded-lg`}>
                                        <p className={`text-xs ${colors.text} mb-1`}>Tags</p>
                                        <p className={`text-2xl font-secondary font-bold ${colors.text}`}>{category.totalTags}</p>
                                    </div>
                                    <div className={`p-3 ${colors.bg} rounded-lg`}>
                                        <p className={`text-xs ${colors.text} mb-1`}>Utilizadores</p>
                                        <p className={`text-2xl font-secondary font-bold ${colors.text}`}>{category.activeUsers}</p>
                                    </div>
                                </div>

                                <div className="space-y-2 mb-4">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-slate-600">Backoffice:</span>
                                        <Badge variant="outline" className={colors.badge}>
                                            {category.backofficeUsers} gestores
                                        </Badge>
                                    </div>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-slate-600">Tickets:</span>
                                        <div className="flex items-center gap-2">
                                            <Badge variant="outline" className="bg-slate-100 text-slate-700">
                                                {category.totalTickets} total
                                            </Badge>
                                            {category.openTickets > 0 && (
                                                <Badge variant="outline" className="bg-amber-100 text-amber-700">
                                                    {category.openTickets} abertos
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-4 border-t border-slate-200">
                                    <p className="text-xs text-slate-600 mb-2">Gestores Responsáveis:</p>
                                    <div className="flex flex-wrap gap-2">
                                        {category.managers.length === 0 ? (
                                            <span className="text-xs text-slate-400 italic">Sem gestores atribuídos</span>
                                        ) : category.managers.map((manager) => (
                                            <Badge key={manager} className={`${colors.bg} ${colors.text} border ${colors.border}`}>
                                                {manager}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>

                                <div className="mt-4 pt-4 border-t border-slate-200 flex gap-2">
                                    <Button variant="outline" size="sm" className="flex-1" onClick={(event) => {
                                        event.stopPropagation();
                                        openDetails(category);
                                    }}>
                                        <Eye size={14} className="mr-2"/>
                                        Ver Detalhes
                                    </Button>
                                    {canUpdateDepartments && (
                                        <Button variant="outline" size="sm" disabled={submitting} onClick={(event) => {
                                            event.stopPropagation();
                                            openEdit(category);
                                        }}>
                                            <Edit size={14}/>
                                        </Button>
                                    )}
                                    {canDeleteDepartments && (
                                        <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" disabled={submitting} onClick={(event) => {
                                            event.stopPropagation();
                                            handleDelete(category);
                                        }}>
                                            <Trash2 size={14}/>
                                        </Button>
                                    )}
                                </div>
                            </Card>
                        );
                    })}
                </div>

                <Dialog open={Boolean(selectedCategory)} onOpenChange={(open) => {
                    if (!open) setSelectedCategory(null);
                }}>
                    <DialogContent className="max-w-3xl">
                        {selectedCategory && (
                            <>
                                <DialogHeader>
                                    <div className="flex items-start gap-4">
                                        <div className={`${selectedCategoryColors.bg} p-3 rounded-xl border ${selectedCategoryColors.border}`}>
                                            {selectedCategoryIconOption?.icon ? (
                                                <img src={selectedCategoryIconOption.icon} alt="" className="w-7 h-7 object-contain"/>
                                            ) : (
                                                <SelectedCategoryIcon className={selectedCategoryColors.icon} size={28}/>
                                            )}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <DialogTitle className="text-xl font-secondary font-bold text-slate-900">
                                                {selectedCategory.name}
                                            </DialogTitle>
                                            <DialogDescription className="mt-1">
                                                {selectedCategory.description || 'Sem descricao registada para esta categoria.'}
                                            </DialogDescription>
                                        </div>
                                    </div>
                                </DialogHeader>

                                <div className="space-y-6 py-2">
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        <div className={`${selectedCategoryColors.bg} rounded-lg p-4 border ${selectedCategoryColors.border}`}>
                                            <p className={`text-xs ${selectedCategoryColors.text} mb-1`}>Tags</p>
                                            <p className={`text-2xl font-secondary font-bold ${selectedCategoryColors.text}`}>
                                                {selectedCategory.totalTags}
                                            </p>
                                        </div>
                                        <div className={`${selectedCategoryColors.bg} rounded-lg p-4 border ${selectedCategoryColors.border}`}>
                                            <p className={`text-xs ${selectedCategoryColors.text} mb-1`}>Utilizadores</p>
                                            <p className={`text-2xl font-secondary font-bold ${selectedCategoryColors.text}`}>
                                                {selectedCategory.activeUsers}
                                            </p>
                                        </div>
                                        <div className={`${selectedCategoryColors.bg} rounded-lg p-4 border ${selectedCategoryColors.border}`}>
                                            <p className={`text-xs ${selectedCategoryColors.text} mb-1`}>Gestores</p>
                                            <p className={`text-2xl font-secondary font-bold ${selectedCategoryColors.text}`}>
                                                {selectedCategory.backofficeUsers}
                                            </p>
                                        </div>
                                        <div className={`${selectedCategoryColors.bg} rounded-lg p-4 border ${selectedCategoryColors.border}`}>
                                            <p className={`text-xs ${selectedCategoryColors.text} mb-1`}>Tickets</p>
                                            <p className={`text-2xl font-secondary font-bold ${selectedCategoryColors.text}`}>
                                                {selectedCategory.totalTickets}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="rounded-lg border border-slate-200 p-4">
                                            <h3 className="text-sm font-semibold text-slate-900 mb-3">Informacao Geral</h3>
                                            <div className="space-y-3 text-sm">
                                                <div className="flex items-center justify-between gap-4">
                                                    <span className="text-slate-500">ID</span>
                                                    <span className="font-medium text-slate-900">{withoutUuid(selectedCategory.id, '-')}</span>
                                                </div>
                                                <div className="flex items-center justify-between gap-4">
                                                    <span className="text-slate-500">Tickets abertos</span>
                                                    <Badge variant="outline" className={selectedCategory.openTickets > 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}>
                                                        {selectedCategory.openTickets}
                                                    </Badge>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="rounded-lg border border-slate-200 p-4">
                                            <h3 className="text-sm font-semibold text-slate-900 mb-3">Aparencia</h3>
                                            <div className="space-y-3 text-sm">
                                                <div className="flex items-center justify-between gap-4">
                                                    <span className="text-slate-500">Icone</span>
                                                    <span className="font-medium text-slate-900">
                                                        {selectedCategoryIconOption?.label || selectedCategory.icon || 'Sem ícone'}
                                                    </span>
                                                </div>
                                                <div className="flex items-center justify-between gap-4">
                                                    <span className="text-slate-500">Cor</span>
                                                    <span className="inline-flex items-center gap-2 font-medium text-slate-900">
                                                        <span
                                                            className="w-3 h-3 rounded-full border border-slate-200"
                                                            style={{backgroundColor: selectedCategoryColorOption?.color || '#3b82f6'}}
                                                        />
                                                        {selectedCategoryColorOption?.label || selectedCategory.color || 'Sem cor'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="rounded-lg border border-slate-200 p-4">
                                        <h3 className="text-sm font-semibold text-slate-900 mb-3">Gestores Responsaveis</h3>
                                        <div className="flex flex-wrap gap-2">
                                            {selectedCategory.managers.length === 0 ? (
                                                <span className="text-sm text-slate-500">Sem gestores atribuidos.</span>
                                            ) : selectedCategory.managers.map((manager) => (
                                                <Badge key={manager} className={`${selectedCategoryColors.bg} ${selectedCategoryColors.text} border ${selectedCategoryColors.border}`}>
                                                    {manager}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <DialogFooter>
                                    <Button variant="outline" onClick={() => setSelectedCategory(null)}>
                                        Fechar
                                    </Button>
                                    {canUpdateDepartments && (
                                        <Button
                                            className="bg-blue-600 hover:bg-blue-700"
                                            onClick={() => {
                                                const category = selectedCategory;
                                                setSelectedCategory(null);
                                                openEdit(category);
                                            }}
                                        >
                                            <Edit size={14} className="mr-2"/>
                                            Editar Categoria
                                        </Button>
                                    )}
                                </DialogFooter>
                            </>
                        )}
                    </DialogContent>
                </Dialog>

                <Card className="p-6 bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200">
                    <div className="flex items-start gap-4">
                        <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg p-3">
                            <Building2 className="text-white" size={28}/>
                        </div>
                        <div className="flex-1">
                            <h3 className="text-slate-900 font-secondary font-bold text-xl mb-3">Como Funciona a Segmentação por Filial</h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-white rounded-lg p-4 border border-slate-200">
                                    <h4 className="font-secondary font-bold text-slate-900 mb-2">1️⃣ Organização de Tags</h4>
                                    <p className="text-sm text-slate-600">Cada tag é atribuída a uma categoria. Ex: Carregador do escritório → Categoria "Escritório"</p>
                                </div>

                                <div className="bg-white rounded-lg p-4 border border-slate-200">
                                    <h4 className="font-secondary font-bold text-slate-900 mb-2">2️⃣ Utilizadores por Categoria</h4>
                                    <p className="text-sm text-slate-600">Utilizadores de veículos são associados à categoria que usam. Ex: Frota elétrica corporativa</p>
                                </div>

                                <div className="bg-white rounded-lg p-4 border border-slate-200">
                                    <h4 className="font-secondary font-bold text-slate-900 mb-2">3️⃣ Permissões por Filial</h4>
                                    <p className="text-sm text-slate-600">Gestor de filial com permissão "Por Categoria" vê apenas sua(s) filial(is) atribuída(s)</p>
                                </div>

                                <div className="bg-white rounded-lg p-4 border border-slate-200">
                                    <h4 className="font-secondary font-bold text-slate-900 mb-2">4️⃣ Dashboards por Filial</h4>
                                    <p className="text-sm text-slate-600">Métricas e relatórios mostram apenas dados da(s) filial(is) que o gestor tem acesso</p>
                                </div>

                                <div className="bg-white rounded-lg p-4 border border-slate-200">
                                    <h4 className="font-secondary font-bold text-slate-900 mb-2">5️⃣ Tickets por Filial</h4>
                                    <p className="text-sm text-slate-600">Tickets criados numa filial são visíveis apenas para gestores dessa filial</p>
                                </div>

                                <div className="bg-white rounded-lg p-4 border border-slate-200">
                                    <h4 className="font-secondary font-bold text-slate-900 mb-2">6️⃣ Hierarquia Clara</h4>
                                    <p className="text-sm text-slate-600">Admin central vê tudo. Gestor de filial vê apenas sua área. Utilizador reporta sem acesso backoffice</p>
                                </div>
                            </div>

                            <div className="mt-4 p-4 bg-white rounded-lg border-2 border-blue-300">
                                <p className="text-sm font-medium text-blue-900 mb-2">💡 Exemplo Prático</p>
                                <p className="text-sm text-slate-700">
                                    <strong>João (Gestor de Filial - Fábrica 1)</strong> → Vê apenas tags, tickets e utilizadores da "Fábrica 1"<br/>
                                    <strong>Maria (Gestora de Filial - Frota Elétrica)</strong> → Vê apenas carregadores e veículos elétricos<br/>
                                    <strong>Admin Central</strong> → Vê e gere todas as filiais/categorias do sistema
                                </p>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        </Layout>
    );
};
